import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { initI18n, t, getLanguage } from './i18n.js';
import { initVanguardAIConsultant } from './ai-consultant.js';

gsap.registerPlugin(ScrollTrigger);

document.addEventListener('DOMContentLoaded', () => {
  initI18n();
  initSmoothScroll();
  initMobileNavigation();
  initHeroVideoScroll();
  initHeroCoverflowCarousel();
  initCounterAnimation();
  initBeforeAfterHover();
  initNavActiveState();
  initVanguardAIConsultant();
  initProjectIntakeForm();
  initScrollEntrances();
  initParallaxSections();
  initHeroDigitalSerenity();
  initRuixenGradientFooter();
});

/* =========================================================
   MOBILE NAVIGATION DRAWER — Accessible, Focus-Trapped, Escape-Key
   ========================================================= */
function initMobileNavigation() {
  const toggleBtn = document.getElementById('mobile-nav-toggle');
  const drawer = document.getElementById('mobile-nav-drawer');
  const closeBtn = document.getElementById('mobile-nav-close');
  if (!toggleBtn || !drawer) return;

  // Guarantee drawer is attached directly to document.body for flawless stacking context & touch events
  if (drawer.parentElement !== document.body) {
    document.body.appendChild(drawer);
  }

  // Create backdrop if not already created
  let backdrop = document.querySelector('.mobile-nav-backdrop');
  if (!backdrop) {
    backdrop = document.createElement('div');
    backdrop.className = 'mobile-nav-backdrop';
    backdrop.setAttribute('aria-hidden', 'true');
    document.body.appendChild(backdrop);
  }

  function openDrawer() {
    drawer.classList.add('is-open');
    backdrop.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    toggleBtn.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    if (closeBtn) closeBtn.focus();
  }

  function closeDrawer() {
    // Use an accelerating ease-in for close — the mirror of the ease-out used on open.
    // This makes the drawer snap away quickly, feeling intentional rather than sluggish.
    drawer.style.transitionTimingFunction = 'cubic-bezier(0.32, 0.72, 0, 1)';
    drawer.addEventListener('transitionend', () => {
      drawer.style.transitionTimingFunction = '';
    }, { once: true });

    drawer.classList.remove('is-open');
    backdrop.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    toggleBtn.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    toggleBtn.focus();
  }

  toggleBtn.addEventListener('click', () => {
    const isOpen = drawer.classList.contains('is-open');
    if (isOpen) closeDrawer();
    else openDrawer();
  });

  if (closeBtn) {
    closeBtn.addEventListener('click', closeDrawer);
  }

  backdrop.addEventListener('click', closeDrawer);

  // Close on Escape key press
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
      closeDrawer();
    }
  });

  // Close drawer when any link inside drawer is clicked
  const drawerInteractiveElements = drawer.querySelectorAll('a, .mobile-nav-link, .mobile-drawer-wa-btn');
  drawerInteractiveElements.forEach(el => {
    el.addEventListener('click', () => {
      closeDrawer();
    });
  });
}

/* =========================================================
   HERO VIDEO — GSAP Scroll-Reactive High Definition Controller
   Smoothly scrubs currentTime in sync with user scroll position,
   while maintaining maximum 1080p native video fidelity.
   ========================================================= */
function initHeroVideoScroll() {
  const video = document.getElementById('hero-video');
  const heroSection = document.querySelector('.hero-universe');
  if (!video || !heroSection) return;

  const isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Function to bind GSAP ScrollTrigger to video playback & depth
  function setupVideoScrub() {
    // If user prefers reduced motion or video duration is not yet available, fallback gracefully
    const duration = video.duration && !isNaN(video.duration) && video.duration > 0 ? video.duration : 6.0;

    if (isReducedMotion) {
      video.play().catch(() => {});
      return;
    }

    // Object proxy for GSAP smooth scrub tweening
    const videoScrubObj = { frame: 0 };

    // GSAP ScrollTrigger scrubbing video.currentTime and adding subtle cinematic 2.5D push
    gsap.to(videoScrubObj, {
      frame: duration,
      ease: 'none',
      scrollTrigger: {
        trigger: heroSection,
        start: 'top top',
        end: 'bottom top',
        scrub: 0.8, // silky smooth inertia scrubbing
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          if (!isNaN(video.duration) && video.readyState >= 1) {
            video.currentTime = Math.min(duration, Math.max(0, self.progress * duration));
          }
        }
      }
    });

    // Subtle scale & cinematic parallax depth reaction on scroll
    gsap.to(video, {
      scale: 1.08,
      yPercent: 8,
      ease: 'none',
      scrollTrigger: {
        trigger: heroSection,
        start: 'top top',
        end: 'bottom top',
        scrub: 1
      }
    });
  }

  // Ensure metadata is loaded so duration is accurate
  if (video.readyState >= 1) {
    setupVideoScrub();
  } else {
    video.addEventListener('loadedmetadata', setupVideoScrub, { once: true });
    // Safety timeout fallback in case of delayed network caching
    setTimeout(() => {
      if (video.readyState >= 1) setupVideoScrub();
    }, 400);
  }
}

/* ==========================================================================
   HERO 3D COVERFLOW CAROUSEL — Apple Coverflow & 21st.dev Component Logic
   ========================================================================== */
const COVERFLOW_SLIDES_DATA = [
  {
    tag: { es: 'SaaS Core', en: 'SaaS Core' },
    title: { es: 'Gestión Operativa & Dashboard', en: 'Core Operations & Dashboard' },
    subtitle: { es: 'Arquitectura SaaS Centralizada · Control de Procesos', en: 'Centralized SaaS Architecture · Process Command' },
    meta: [
      { label: { es: 'Validación', en: 'Validation' }, value: { es: 'Prototipo Semana 2', en: 'Week 2 Live Prototype' } },
      { label: { es: 'Arquitectura', en: 'Architecture' }, value: { es: 'TypeScript · Node · SQL', en: 'TypeScript · Node · SQL' } },
      { label: { es: 'Propiedad', en: 'Ownership' }, value: { es: '100% Código Propio', en: '100% Proprietary Code' } }
    ]
  },
  {
    tag: { es: 'Fintech', en: 'Fintech' },
    title: { es: 'Banca & Finanzas en Tiempo Real', en: 'Real-Time Banking & Finance' },
    subtitle: { es: 'Pasarelas transaccionales seguras y liquidación continua', en: 'High-security payment gateways & instant settlement' },
    meta: [
      { label: { es: 'Seguridad', en: 'Security' }, value: { es: 'Cifrado Bancario AES-256', en: 'AES-256 Banking Encryption' } },
      { label: { es: 'Latencia', en: 'Latency' }, value: { es: '< 120ms por transacción', en: '< 120ms per transaction' } },
      { label: { es: 'Licencia', en: 'Licensing' }, value: { es: 'Cero rentas mensuales', en: 'Zero monthly SaaS fees' } }
    ]
  },
  {
    tag: { es: 'Logística', en: 'Logistics' },
    title: { es: 'Portal Logístico & Control de Flota', en: 'Logistics Portal & Fleet Telemetry' },
    subtitle: { es: 'Trazabilidad de rutas, inventario central y despacho', en: 'Route tracking, central inventory & dispatch' },
    meta: [
      { label: { es: 'Monitoreo', en: 'Monitoring' }, value: { es: 'Telemetría GPS en Vivo', en: 'Live GPS Telemetry' } },
      { label: { es: 'Capacidad', en: 'Scalability' }, value: { es: 'Multi-almacén Cloud', en: 'Cloud Multi-Warehouse' } },
      { label: { es: 'Soporte', en: 'Support' }, value: { es: 'Directo con Ingenieros', en: 'Senior Engineers Direct' } }
    ]
  },
  {
    tag: { es: 'Hospitality', en: 'Hospitality' },
    title: { es: 'Smart Gastronomía & Comandas', en: 'Smart Hospitality & Kitchen Orders' },
    subtitle: { es: 'Sincronización de pedidos en sala y cocina al instante', en: 'Instant table ordering & kitchen flow sync' },
    meta: [
      { label: { es: 'Sincronización', en: 'Sync' }, value: { es: 'WebSockets en Vivo', en: 'Real-Time WebSockets' } },
      { label: { es: 'Dispositivos', en: 'Hardware' }, value: { es: 'TPVs, Tablets & Móvil', en: 'POS, Tablets & Mobile' } },
      { label: { es: 'Entrega', en: 'Delivery' }, value: { es: 'Semana 2 Navegable', en: 'Week 2 Clickable' } }
    ]
  },
  {
    tag: { es: 'HealthTech', en: 'HealthTech' },
    title: { es: 'Portal Clínico & Expedientes Médicos', en: 'Clinical Portal & Patient Records' },
    subtitle: { es: 'Historiales encriptados y permisos por rol médico', en: 'Encrypted EHR and strict role-based access' },
    meta: [
      { label: { es: 'Privacidad', en: 'Compliance' }, value: { es: 'Estándar Médico HIPAA', en: 'HIPAA Medical Standards' } },
      { label: { es: 'Auditoría', en: 'Audit' }, value: { es: 'Trazabilidad de Acceso', en: 'Full Access Logging' } },
      { label: { es: 'Propiedad', en: 'Ownership' }, value: { es: '100% de tu Empresa', en: '100% Client Owned' } }
    ]
  },
  {
    tag: { es: 'Medicina', en: 'Healthcare' },
    title: { es: 'Agendamiento & Portal Odontológico', en: 'Dental Care & Auto-Scheduling' },
    subtitle: { es: 'Citas automatizadas y recordatorios multicanal', en: 'Automated booking & multichannel reminders' },
    meta: [
      { label: { es: 'Retención', en: 'Retention' }, value: { es: '+45% asistencia confirmada', en: '+45% patient attendance' } },
      { label: { es: 'Integración', en: 'Integration' }, value: { es: 'WhatsApp API Directo', en: 'Direct WhatsApp API' } },
      { label: { es: 'Diseño', en: 'Design' }, value: { es: 'Editorial Ultra-rápido', en: 'Instant Editorial UI' } }
    ]
  },
  {
    tag: { es: 'Enterprise', en: 'Enterprise' },
    title: { es: 'Suite ERP & Finanzas Corporativas', en: 'Enterprise ERP & Core Finance' },
    subtitle: { es: 'Flujos contables, inventario y facturación centralizada', en: 'Accounting workflows, billing & inventory sync' },
    meta: [
      { label: { es: 'Integración', en: 'Integration' }, value: { es: 'Bancos y Factura Electrónica', en: 'Banks & Tax Authorities' } },
      { label: { es: 'Volumen', en: 'Volume' }, value: { es: 'Millones de Registros', en: 'Millions of Rows' } },
      { label: { es: 'Rentas', en: 'Licensing' }, value: { es: 'Cero costes por usuario', en: 'Zero per-seat fees' } }
    ]
  },
  {
    tag: { es: 'Automatización', en: 'Automation' },
    title: { es: 'Automatización de Flujos de Datos', en: 'Continuous Data Workflow Automation' },
    subtitle: { es: 'Eliminación del trabajo manual entre departamentos', en: 'Eliminating repetitive manual cross-team tasks' },
    meta: [
      { label: { es: 'Impacto', en: 'Efficiency' }, value: { es: '-70% Carga Operativa', en: '-70% Manual Workload' } },
      { label: { es: 'Disponibilidad', en: 'Uptime' }, value: { es: '24/7 sin Interrupciones', en: '24/7 Continuous Run' } },
      { label: { es: 'Garantía', en: 'Guarantee' }, value: { es: 'Prototipo Semana 2', en: 'Week 2 Live Prototype' } }
    ]
  }
];

function initHeroCoverflowCarousel() {
  const container = document.getElementById('hero-coverflow') || document.querySelector('.hero-coverflow-container');
  const frame = document.getElementById('coverflow-frame');
  const track = document.getElementById('coverflow-track');
  const prevBtn = document.getElementById('carousel-prev');
  const nextBtn = document.getElementById('carousel-next');
  const indicatorsContainer = document.getElementById('stage-indicators');
  if (!container || !frame || !track) return;

  const cards = Array.from(track.querySelectorAll('.coverflow-card'));
  const count = cards.length;
  if (count === 0) return;

  // Parámetros de Perspectiva Curva Panorámica (Anfiteatro Cóncavo / Arc Ribbon)
  const rotateYMax = 32;     // Giro sutil hacia el centro del arco
  const curveZ = 75;         // Profundidad de arco hacia atrás para laterales
  const curveY = 32;         // Elevación parabólica cóncava (forma de U suave)
  const pitchFactor = 0.88;  // Superposición / solape elegante entre tarjetas contiguas
  const perspective = 1600;  // Distancia de lente panorámica abierta
  const loop = true;

  let pos = 0;
  let target = 0;
  let width = 0;
  let rafId = null;
  let drag = null;
  let selected = 0;
  let autoPlayTimer = null;

  const indexAt = (p) => ((Math.round(p) % count) + count) % count;

  function measure() {
    const card = cards[0];
    if (!card) return;
    width = card.offsetWidth;
    paint();
  }

  // Renderizado suave y fluido en GPU de la curvatura panorámica
  function paint() {
    if (!width) return;
    const pitch = width * pitchFactor;

    cards.forEach((card, index) => {
      let offset = index - pos;
      if (loop) {
        offset = ((offset % count) + count) % count;
        if (offset > count / 2) offset -= count;
      }

      const distance = Math.abs(offset);

      // Curvatura Parabólica en X, Y y Z
      // Los laterales se elevan ligeramente y retroceden en Z, formando la curva cóncava
      const rotateY = Math.sign(offset) * Math.min(Math.abs(offset) * 11, rotateYMax);
      const translateZ = -Math.pow(distance, 1.45) * curveZ;
      const translateY = -Math.pow(distance, 1.4) * curveY;

      // Escala gradual para crear profundidad fotográfica natural
      const scale = Math.max(0.72, 1 - distance * 0.07);

      card.style.transform =
        `translateX(calc(-50% + ${(offset * pitch).toFixed(2)}px)) ` +
        `translateY(${translateY.toFixed(2)}px) ` +
        `translateZ(${translateZ.toFixed(2)}px) ` +
        `rotateY(${(-rotateY).toFixed(2)}deg) ` +
        `scale(${scale.toFixed(3)})`;

      // Visibilidad y z-index ordenado hacia los bordes
      const edge = loop ? Math.min(1, Math.max(0, count / 2 - distance + 0.3)) : 1;
      const opacity = Math.max(0.2, 1 - distance * 0.16) * edge;
      card.style.opacity = opacity.toFixed(3);
      card.style.zIndex = String(100 - Math.round(distance * 10));

      if (distance < 0.45) {
        card.classList.add('is-center');
      } else {
        card.classList.remove('is-center');
      }
    });
  }

  function updateCaption(idx) {
    const lang = document.documentElement.lang === 'en' ? 'en' : 'es';
    const data = COVERFLOW_SLIDES_DATA[idx];
    if (!data) return;

    const tagEl = document.getElementById('cf-tag');
    const titleEl = document.getElementById('cf-title');
    const subtitleEl = document.getElementById('cf-subtitle');

    if (tagEl) tagEl.textContent = data.tag[lang] || data.tag.es;
    if (titleEl) titleEl.textContent = data.title[lang] || data.title.es;
    if (subtitleEl) subtitleEl.textContent = data.subtitle[lang] || data.subtitle.es;

    if (indicatorsContainer) {
      const dots = indicatorsContainer.querySelectorAll('.stage-dot');
      dots.forEach((d, i) => d.classList.toggle('active', i === idx));
    }
  }

  function setSelected(idx) {
    if (idx === selected) return;
    selected = idx;
    updateCaption(idx);
  }

  function clamp(p) {
    return loop ? p : Math.max(0, Math.min(count - 1, p));
  }

  function settle(toTarget) {
    if (rafId !== null) cancelAnimationFrame(rafId);
    target = toTarget;
    setSelected(indexAt(toTarget));

    const step = () => {
      const remaining = target - pos;
      if (Math.abs(remaining) < 0.0004) {
        pos = target;
        paint();
        rafId = null;
        return;
      }
      pos += remaining * 0.16;
      paint();
      rafId = requestAnimationFrame(step);
    };
    rafId = requestAnimationFrame(step);
  }

  function goTo(index) {
    const toTarget = loop
      ? index + Math.round((target - index) / count) * count
      : index;
    settle(clamp(toTarget));
  }

  function nudge(by) {
    settle(clamp(Math.round(target) + by));
    resetAutoPlay();
  }

  // Pointer drag gestures con PointerCapture oficial
  frame.addEventListener('pointerdown', (e) => {
    if (e.target.closest('.coverflow-nav-btn')) return;
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    frame.setPointerCapture(e.pointerId);
    target = pos;
    drag = {
      id: e.pointerId,
      x: e.clientX,
      pos: pos,
      v: 0,
      t: performance.now()
    };
    frame.classList.add('is-dragging');
    stopAutoPlay();
  });

  frame.addEventListener('pointermove', (e) => {
    if (!drag || drag.id !== e.pointerId) return;
    const pitch = width * pitchFactor;
    if (!pitch) return;

    const now = performance.now();
    const prev = pos;
    pos = clamp(drag.pos - (e.clientX - drag.x) / pitch);
    drag.v = ((pos - prev) / Math.max(now - drag.t, 1)) * 1000;
    drag.t = now;

    const idx = indexAt(pos);
    if (idx !== selected) setSelected(idx);
    paint();
  });

  const endDrag = (e) => {
    if (!drag || drag.id !== e.pointerId) return;
    const velocity = drag.v;
    drag = null;
    frame.classList.remove('is-dragging');
    const carried = Math.max(-2, Math.min(2, velocity * 0.18));
    settle(clamp(Math.round(pos + carried)));
    resetAutoPlay();
  };

  frame.addEventListener('pointerup', endDrag);
  frame.addEventListener('pointercancel', endDrag);

  // Navegación por teclado (Flechas)
  frame.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      nudge(-1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      nudge(1);
    }
  });

  // Clic en tarjetas
  cards.forEach((card, index) => {
    card.addEventListener('click', (e) => {
      let offset = index - pos;
      if (loop) {
        offset = ((offset % count) + count) % count;
        if (offset > count / 2) offset -= count;
      }
      if (Math.abs(offset) > 0.45) {
        e.preventDefault();
        goTo(index);
        resetAutoPlay();
      }
    });
  });

  if (prevBtn) prevBtn.addEventListener('click', () => nudge(-1));
  if (nextBtn) nextBtn.addEventListener('click', () => nudge(1));

  // Generación de indicadores / dots
  if (indicatorsContainer) {
    indicatorsContainer.innerHTML = '';
    for (let i = 0; i < count; i++) {
      const dot = document.createElement('button');
      dot.className = `stage-dot ${i === 0 ? 'active' : ''}`;
      dot.type = 'button';
      dot.setAttribute('aria-label', `Ir a proyecto ${i + 1}`);
      dot.addEventListener('click', () => {
        goTo(i);
        resetAutoPlay();
      });
      indicatorsContainer.appendChild(dot);
    }
  }

  const observer = new ResizeObserver(measure);
  observer.observe(frame);

  measure();
  updateCaption(0);

  // Auto-play continuo con pausa inteligente
  function startAutoPlay() {
    stopAutoPlay();
    autoPlayTimer = setInterval(() => {
      if (window.scrollY < 1200 && !document.hidden && !drag) {
        nudge(1);
      }
    }, 5500);
  }

  function stopAutoPlay() {
    if (autoPlayTimer) clearInterval(autoPlayTimer);
    autoPlayTimer = null;
  }

  function resetAutoPlay() {
    startAutoPlay();
  }

  container.addEventListener('mouseenter', stopAutoPlay);
  container.addEventListener('mouseleave', startAutoPlay);
  startAutoPlay();

  window.addEventListener('vanguard:langchange', () => {
    updateCaption(selected);
  });
}

/* =========================================================
   Smooth Scroll para todos los anchors internos
   ========================================================= */
function initSmoothScroll() {
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function(e) {
      const href = this.getAttribute('href');
      if (href === '#' || !href) return;

      const target = document.querySelector(href);
      if (target) {
        e.preventDefault();
        target.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    });
  });
}

/* =========================================================
   Counter Animation — anima métricas al entrar al viewport
   ========================================================= */
function initCounterAnimation() {
  const metricValues = document.querySelectorAll('.metric-value[data-counter-target]');
  if (!metricValues.length) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        animateCounter(entry.target);
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.5 });

  metricValues.forEach(el => observer.observe(el));
}

function animateCounter(el) {
  const target = parseFloat(el.dataset.counterTarget);
  const suffix = el.dataset.counterSuffix || '';
  const prefix = el.dataset.counterPrefix || '';
  const isDecimal = target % 1 !== 0;
  const duration = 1400;
  const startTime = performance.now();

  function step(currentTime) {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const eased = 1 - Math.pow(1 - progress, 4);
    const current = isDecimal
      ? (eased * target).toFixed(1)
      : Math.round(eased * target);

    el.textContent = prefix + current + suffix;

    if (progress < 1) {
      requestAnimationFrame(step);
    } else {
      el.textContent = prefix + (isDecimal ? target.toFixed(1) : target) + suffix;
    }
  }

  requestAnimationFrame(step);
}

/* =========================================================
   Before/After — micro-interacción hover en columnas
   ========================================================= */
function initBeforeAfterHover() {
  const grid = document.getElementById('before-after');
  if (!grid) return;

  const beforeCol = grid.querySelector('.before-column');
  const afterCol = grid.querySelector('.after-column');

  if (beforeCol && afterCol) {
    beforeCol.addEventListener('mouseenter', () => {
      afterCol.style.opacity = '0.6';
      afterCol.style.transition = 'opacity 0.25s ease';
    });
    beforeCol.addEventListener('mouseleave', () => { afterCol.style.opacity = '1'; });
    afterCol.addEventListener('mouseenter', () => {
      beforeCol.style.opacity = '0.6';
      beforeCol.style.transition = 'opacity 0.25s ease';
    });
    afterCol.addEventListener('mouseleave', () => { beforeCol.style.opacity = '1'; });
  }
}

/* =========================================================
   Nav Active State — resalta la sección activa
   ========================================================= */
function initNavActiveState() {
  const sections = ['#inicio', '#sistema', '#resultados', '#seguridad', '#contacto'];
  const navLinks = document.querySelectorAll('.portal-nav-link');
  const sectionEls = sections.map(id => document.querySelector(id)).filter(Boolean);
  if (!sectionEls.length) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const id = '#' + entry.target.id;
        navLinks.forEach(link => {
          if (link.getAttribute('href') === id) {
            link.classList.add('active-pill');
          } else {
            link.classList.remove('active-pill');
          }
        });
      }
    });
  }, { threshold: 0.3 });

  sectionEls.forEach(el => observer.observe(el));
}

/* =========================================================
   Project Intake Form — Manejo del Briefing en Tiempo Récord
   ========================================================= */
function initProjectIntakeForm() {
  const form = document.getElementById('project-intake-form');
  const statusBox = document.getElementById('intake-status');
  const submitBtn = document.getElementById('btn-submit-intake');
  const heroBtn = document.getElementById('btn-hero-primary');

  // Hero CTA: Al hacer clic, hace scroll suave a #contacto y enfoca el consultor IA
  if (heroBtn) {
    heroBtn.addEventListener('click', (e) => {
      e.preventDefault();
      const contactSec = document.getElementById('contacto');
      if (contactSec) {
        contactSec.scrollIntoView({ behavior: 'smooth' });
        setTimeout(() => {
          const aiInput = document.getElementById('ai-user-text');
          if (aiInput) aiInput.focus();
        }, 500);
      }
    });
  }

  // Radio Pills Selection
  const pillLabels = document.querySelectorAll('.system-pill');
  pillLabels.forEach(label => {
    label.addEventListener('click', () => {
      pillLabels.forEach(p => p.classList.remove('active'));
      label.classList.add('active');
    });
  });

  if (!form) return;

  // Clear invalid states on input
  const inputs = form.querySelectorAll('.form-control');
  inputs.forEach(input => {
    input.addEventListener('input', () => {
      if (input.classList.contains('is-invalid')) {
        input.classList.remove('is-invalid');
      }
    });
  });

  // Form Submit Handler
  form.addEventListener('submit', (e) => {
    e.preventDefault();

    // Validation
    const name = form.name.value.trim();
    const email = form.email.value.trim();
    const company = form.company.value.trim();
    const phone = form.phone.value.trim();
    const details = form.details.value.trim();
    const timeline = form.timeline.value;
    const budget = form.budget.value.trim() || 'A convenir según alcance y propuesta técnica';
    const systemType = form.querySelector('input[name="system_type"]:checked')?.value || 'Sistema Web a Medida';

    let hasError = false;
    let firstInvalid = null;

    [
      { field: form.name, val: name },
      { field: form.email, val: email },
      { field: form.company, val: company },
      { field: form.phone, val: phone },
      { field: form.details, val: details }
    ].forEach(({ field, val }) => {
      if (!val) {
        field.classList.add('is-invalid');
        hasError = true;
        if (!firstInvalid) firstInvalid = field;
      }
    });

    if (hasError) {
      if (firstInvalid) firstInvalid.focus();
      return;
    }

    // Processing State
    if (submitBtn) {
      submitBtn.disabled = true;
      const btnText = submitBtn.querySelector('.btn-text');
      if (btnText) btnText.textContent = t('form.processing');
    }

    const currentLang = getLanguage();
    const isEn = currentLang === 'en';

    // Build mailto body for client backup
    const subject = encodeURIComponent(
      isEn
        ? `New Project Intake in Record Time — ${company} (${name})`
        : `Nuevo Proyecto en Tiempo Récord — ${company} (${name})`
    );
    const body = encodeURIComponent(
      isEn
        ? `Hello Vanguard team,\n\n` +
          `I have submitted the project brief on vanguardsoft.app:\n\n` +
          `• Name: ${name}\n` +
          `• Email: ${email}\n` +
          `• Phone/WhatsApp: ${phone}\n` +
          `• Company/Project: ${company}\n` +
          `• System Type: ${systemType}\n` +
          `• Desired Timeline: ${timeline}\n` +
          `• Estimated Budget: ${budget}\n\n` +
          `Project Details & Challenges:\n${details}\n\n` +
          `Looking forward to connecting with your technical team.`
        : `Hola equipo Vanguard,\n\n` +
          `He completado el formulario de proyecto en vanguardsoft.app:\n\n` +
          `• Nombre: ${name}\n` +
          `• Correo: ${email}\n` +
          `• Teléfono/WhatsApp: ${phone}\n` +
          `• Empresa/Proyecto: ${company}\n` +
          `• Tipo de Sistema: ${systemType}\n` +
          `• Plazo deseado: ${timeline}\n` +
          `• Presupuesto orientativo: ${budget}\n\n` +
          `Detalles y Retos:\n${details}\n\n` +
          `Quedo a la espera de su contacto técnico y propuesta funcional.`
    );

    // Simulate instant secure processing
    setTimeout(() => {
      if (statusBox) {
        statusBox.className = 'intake-status-box success';
        statusBox.innerHTML = `
          <div style="width: 44px; height: 44px; border-radius: 50%; background: #dcfce7; display: flex; align-items: center; justify-content: center; margin: 0 auto 12px auto;" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#15803d" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
          </div>
          <div class="status-title">${t('form.success_title')}</div>
          <p>
            ${t('form.success_msg_p1', { name: escapeHtml(name), company: escapeHtml(company), systemType: escapeHtml(systemType) })}
          </p>
          <p style="margin-top: 10px; font-size: 0.875rem; color: #166534;">
            ${t('form.success_msg_p2', { email: escapeHtml(email), phone: escapeHtml(phone) })}
          </p>
          <div style="margin-top: 16px;">
            <a href="mailto:hola@vanguardsoft.app?subject=${subject}&body=${body}" style="display: inline-block; font-size: 0.8125rem; font-weight: 600; color: #007aff; text-decoration: underline; cursor: pointer;">
              ${t('form.success_mailto_copy')}
            </a>
          </div>
        `;
        statusBox.style.display = 'block';
        statusBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      // Reset form
      form.reset();
      pillLabels.forEach((p, idx) => {
        if (idx === 0) p.classList.add('active');
        else p.classList.remove('active');
      });

      if (submitBtn) {
        submitBtn.disabled = false;
        const btnText = submitBtn.querySelector('.btn-text');
        if (btnText) btnText.textContent = t('form.submit_another');
      }
    }, 600);
  });
}

function escapeHtml(str) {
  return str.replace(/[&<>'"]/g, 
    tag => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[tag] || tag)
  );
}

/* =========================================================
   SCROLL ENTRANCE ANIMATIONS — Staggered, Polished, Accessible
   ========================================================= */
function initScrollEntrances() {
  const isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // --- Opp #1: Benefit cards (Section 2) ---
  const benefitCards = document.querySelectorAll('.benefit-cards-grid .benefit-card');
  if (benefitCards.length) {
    gsap.from(benefitCards, {
      opacity: 0,
      y: isReducedMotion ? 0 : 28,
      duration: 0.6,
      stagger: 0.12,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: '.benefit-cards-grid',
        start: 'top 85%',
        toggleActions: 'play none none none'
      }
    });
  }

  // --- Opp #2: Metric badge cards (Section 2 & 6) ---
  const metricCards = document.querySelectorAll('.metric-badge-card');
  if (metricCards.length) {
    gsap.from(metricCards, {
      opacity: 0,
      y: isReducedMotion ? 0 : 22,
      duration: 0.55,
      stagger: 0.08,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: '.metrics-row',
        start: 'top 84%',
        toggleActions: 'play none none none'
      }
    });
  }

  // --- Opp #3: Process steps — stagger sequence ---
  const processSteps = document.querySelectorAll('.process-step');
  const processConnectors = document.querySelectorAll('.process-connector');
  if (processSteps.length) {
    const processTargets = [];
    processSteps.forEach((step, i) => {
      processTargets.push(step);
      if (processConnectors[i]) processTargets.push(processConnectors[i]);
    });

    gsap.from(processTargets, {
      opacity: 0,
      x: isReducedMotion ? 0 : -16,
      duration: 0.5,
      stagger: 0.09,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: '.process-steps-row',
        start: 'top 82%',
        toggleActions: 'play none none none'
      }
    });
  }

  // --- Opp #4: Comparison columns (Section 3) ---
  const comparisonCols = document.querySelectorAll('.comparison-col');
  if (comparisonCols.length) {
    gsap.from(comparisonCols, {
      opacity: 0,
      y: isReducedMotion ? 0 : 30,
      duration: 0.6,
      stagger: 0.14,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: '.comparison-grid',
        start: 'top 85%',
        toggleActions: 'play none none none'
      }
    });
  }

  // --- Opp #5: Testimonial cards ---
  const testimonialCards = document.querySelectorAll('.testimonial-card');
  if (testimonialCards.length) {
    gsap.from(testimonialCards, {
      opacity: 0,
      y: isReducedMotion ? 0 : 24,
      scale: isReducedMotion ? 1 : 0.97,
      duration: 0.5,
      stagger: 0.10,
      ease: 'power2.out',
      transformOrigin: 'top center',
      scrollTrigger: {
        trigger: '.testimonials-grid',
        start: 'top 82%',
        toggleActions: 'play none none none'
      }
    });
  }

  // --- Opp #6: Intake & AI Consultant container entrance ---
  const contactStage = document.getElementById('contacto');
  if (contactStage) {
    gsap.from('.portal-cta-inner, .ai-consultant-card', {
      opacity: 0,
      y: isReducedMotion ? 0 : 25,
      duration: 0.65,
      stagger: 0.15,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: contactStage,
        start: 'top 80%',
        toggleActions: 'play none none none'
      }
    });
  }
}

/* ==========================================================================
   HELPER: Segmentación de Texto en Caracteres respetando palabras & formato
   ========================================================================== */
function splitTextIntoChars(rootNode) {
  const charArray = [];
  const frag = document.createDocumentFragment();

  function traverse(node, isEm = false) {
    if (node.nodeType === Node.TEXT_NODE) {
      const tokens = node.textContent.split(/(\s+)/);
      const textFrag = document.createDocumentFragment();

      tokens.forEach(token => {
        if (!token) return;
        if (/^\s+$/.test(token)) {
          const spaceSpan = document.createElement('span');
          spaceSpan.className = 'char-space';
          spaceSpan.innerHTML = '&nbsp;';
          textFrag.appendChild(spaceSpan);
        } else {
          const wordSpan = document.createElement('span');
          wordSpan.className = 'char-word';
          for (const ch of token) {
            const charSpan = document.createElement('span');
            charSpan.className = 'char';
            if (isEm) charSpan.classList.add('is-em');
            charSpan.textContent = ch;
            wordSpan.appendChild(charSpan);
            charArray.push(charSpan);
          }
          textFrag.appendChild(wordSpan);
        }
      });
      return textFrag;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const isItalic = node.tagName.toLowerCase() === 'em';
      const elem = document.createElement(node.tagName.toLowerCase());
      Array.from(node.childNodes).forEach(child => elem.appendChild(traverse(child, isItalic)));
      return elem;
    }
    return node.cloneNode(true);
  }

  Array.from(rootNode.childNodes).forEach(child => frag.appendChild(traverse(child, false)));
  rootNode.innerHTML = '';
  rootNode.appendChild(frag);
  return charArray;
}

/* =========================================================
   PARALLAX SECTIONS & SCROLL-DRIVEN LETTER REVEAL ENGINE
   1. Hero Text Parallax a 42% de velocidad (0.42) + Fade progresivo
   2. Scroll Scrubbing orgánico carácter por carácter en títulos de secciones
   3. Desplazamiento multicapa con GPU acceleration (translate3d)
   ========================================================= */
function initParallaxSections() {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // 1. Elementos del Hero
  const heroSection = document.querySelector('.hero-universe');
  const heroContent = document.querySelector('.hero-content');

  // 2. Elementos de secciones editoriales para Scrubbing carácter por carácter
  let sectionEntries = [];

  function setupLetterSplitting() {
    const titleElements = document.querySelectorAll('.editorial-h2');
    sectionEntries = [];

    titleElements.forEach(title => {
      const chars = splitTextIntoChars(title);
      const parentSection = title.closest('.section-block') || title.parentElement;
      sectionEntries.push({ title, chars, parentSection });
    });
  }

  setupLetterSplitting();

  // Escuchar cambio de idioma para volver a generar las letras traducidas
  window.addEventListener('vanguard:langchange', () => {
    setupLetterSplitting();
    updateScrollVisuals();
  });

  // Capa 2: Tarjetas, grillas y módulos interactivos con profundidad diferencial en scroll
  const sections = document.querySelectorAll('.section-block, .portal-cta-wrap');
  if (sections.length && !prefersReducedMotion) {
    sections.forEach((section) => {
      const cards = section.querySelectorAll(
        '.benefit-cards-grid, .automation-showcase, .comparison-grid, .testimonials-grid, .metrics-row, .ai-consultant-card, .portal-form-card, .process-steps-row'
      );
      if (cards.length) {
        gsap.to(cards, {
          y: -44,
          ease: 'none',
          scrollTrigger: {
            trigger: section,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 1.2,
            invalidateOnRefresh: true
          }
        });
      }
    });
  }

  // Loop de renderizado reactivo sincronizado con requestAnimationFrame
  function updateScrollVisuals() {
    const scrollY = window.scrollY;
    const vh = window.innerHeight;

    // --- EFECTO 1: Hero Parallax (Desplazamiento a 0.42 y Fade Progresivo) ---
    if (heroSection && heroContent && !prefersReducedMotion) {
      const heroRect = heroSection.getBoundingClientRect();
      if (heroRect.bottom > 0) {
        const heroHeight = heroRect.height || 850;
        const parallaxOffset = scrollY * 0.42;
        const fadeOut = Math.max(0, 1 - (scrollY / (heroHeight * 0.78)));

        heroContent.style.transform = `translate3d(0, -${parallaxOffset.toFixed(2)}px, 0)`;
        heroContent.style.opacity = fadeOut.toFixed(3);
      }
    }

    // --- EFECTO 2: Scroll Scrubbing carácter por carácter en cada sección ---
    sectionEntries.forEach(({ chars, parentSection }) => {
      if (!parentSection || !chars.length) return;
      const sRect = parentSection.getBoundingClientRect();

      if (sRect.top < vh && sRect.bottom > 0) {
        const scrubStart = vh * 0.86;
        const scrubEnd = vh * 0.18;
        const scrubProgress = Math.min(1, Math.max(0, (scrubStart - sRect.top) / (scrubStart - scrubEnd)));

        const totalChars = chars.length;
        chars.forEach((charEl, idx) => {
          if (prefersReducedMotion) {
            charEl.style.opacity = '1';
            charEl.style.transform = 'none';
            return;
          }

          const charStart = (idx / totalChars) * 0.90;
          const charWindow = (1 / totalChars) * 2.6; // Suave solapamiento humano

          let charVal = 0;
          if (scrubProgress <= charStart) {
            charVal = 0;
          } else if (scrubProgress >= charStart + charWindow) {
            charVal = 1;
          } else {
            charVal = (scrubProgress - charStart) / charWindow;
          }

          const opacity = 0.12 + (charVal * 0.88);
          const translateY = (1 - charVal) * 8;
          const scale = 0.95 + (charVal * 0.05);

          charEl.style.opacity = opacity.toFixed(3);
          charEl.style.transform = `translate3d(0, ${translateY.toFixed(2)}px, 0) scale(${scale.toFixed(3)})`;

          if (charVal > 0.65) {
            charEl.classList.add('revealed');
          } else {
            charEl.classList.remove('revealed');
          }
        });
      }
    });
  }

  let ticking = false;
  window.addEventListener('scroll', () => {
    if (!ticking) {
      window.requestAnimationFrame(() => {
        updateScrollVisuals();
        ticking = false;
      });
      ticking = true;
    }
  }, { passive: true });

  // Ejecución inicial para establecer estados correctos al cargar
  updateScrollVisuals();
  ScrollTrigger.refresh();
}

/* =========================================================
   DIGITAL SERENITY — Hero Letter & Word Emergence & Reactivity
   - Word & character splitting with graceful fallback
   - Calm, organic emergence wave on page load
   - Re-triggers when returning to home / clicking logo or #inicio
   - Interactive mouse-following serene glow & character hover float
   ========================================================= */
function initHeroDigitalSerenity() {
  const heroTitle = document.querySelector('.hero-editorial-title');
  const heroDesc = document.querySelector('.hero-editorial-desc');
  const heroCta = document.querySelector('.hero-cta-wrapper');
  const heroUniverse = document.querySelector('.hero-universe');
  if (!heroTitle || !heroDesc) return;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Add mouse glow stage to hero universe if not present
  let mouseGlow = document.querySelector('.hero-serenity-mouse-glow');
  if (!mouseGlow && heroUniverse) {
    mouseGlow = document.createElement('div');
    mouseGlow.className = 'hero-serenity-mouse-glow';
    mouseGlow.setAttribute('aria-hidden', 'true');
    heroUniverse.appendChild(mouseGlow);
  }

  // Preserve initial raw texts for clean re-splitting
  function splitTitleIntoLetters() {
    const text = heroTitle.textContent.trim();
    if (!text) return [];

    const words = text.split(/\s+/);
    const frag = document.createDocumentFragment();
    const chars = [];

    words.forEach((word, wIdx) => {
      const wordSpan = document.createElement('span');
      wordSpan.className = 'serenity-word';

      for (let i = 0; i < word.length; i++) {
        const char = word[i];
        const charSpan = document.createElement('span');
        charSpan.className = 'serenity-char';
        charSpan.textContent = char;
        wordSpan.appendChild(charSpan);
        chars.push(charSpan);
      }

      frag.appendChild(wordSpan);

      if (wIdx < words.length - 1) {
        const space = document.createElement('span');
        space.className = 'serenity-space';
        space.innerHTML = '&nbsp;';
        frag.appendChild(space);
      }
    });

    heroTitle.innerHTML = '';
    heroTitle.appendChild(frag);
    return chars;
  }

  function splitDescIntoWords() {
    const text = heroDesc.textContent.trim();
    if (!text) return [];

    const words = text.split(/\s+/);
    const frag = document.createDocumentFragment();
    const wordElements = [];

    words.forEach((word, wIdx) => {
      const wordSpan = document.createElement('span');
      wordSpan.className = 'serenity-desc-word';
      wordSpan.textContent = word;
      frag.appendChild(wordSpan);
      wordElements.push(wordSpan);

      if (wIdx < words.length - 1) {
        const space = document.createElement('span');
        space.className = 'serenity-space';
        space.innerHTML = '&nbsp;';
        frag.appendChild(space);
      }
    });

    heroDesc.innerHTML = '';
    heroDesc.appendChild(frag);
    return wordElements;
  }

  let titleChars = splitTitleIntoLetters();
  let descWords = splitDescIntoWords();

  // Function to run the Serenity reveal animation
  function runSerenityEntrance() {
    if (prefersReducedMotion) {
      if (titleChars.length) titleChars.forEach(c => { c.style.opacity = '1'; c.style.transform = 'none'; c.style.filter = 'none'; });
      if (descWords.length) descWords.forEach(w => { w.style.opacity = '1'; w.style.transform = 'none'; });
      if (heroCta) { heroCta.style.opacity = '1'; heroCta.style.transform = 'none'; }
      return;
    }

    // Kill any existing tween on these elements
    gsap.killTweensOf(titleChars);
    gsap.killTweensOf(descWords);
    if (heroCta) gsap.killTweensOf(heroCta);

    // Initial state
    gsap.set(titleChars, {
      opacity: 0,
      y: 22,
      scale: 0.94,
      filter: 'blur(10px)'
    });

    gsap.set(descWords, {
      opacity: 0,
      y: 16,
      filter: 'blur(6px)'
    });

    if (heroCta) {
      gsap.set(heroCta, {
        opacity: 0,
        y: 18,
        scale: 0.97
      });
    }

    // Staggered Serenity Wave
    const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

    tl.to(titleChars, {
      opacity: 1,
      y: 0,
      scale: 1,
      filter: 'blur(0px)',
      duration: 0.7,
      stagger: 0.018, // 18ms per character produces the signature calming ripple
      ease: 'power3.out'
    }, 0.1);

    tl.to(descWords, {
      opacity: 0.85,
      y: 0,
      filter: 'blur(0px)',
      duration: 0.6,
      stagger: 0.035,
      ease: 'power2.out'
    }, 0.35);

    if (heroCta) {
      tl.to(heroCta, {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.6,
        ease: 'back.out(1.2)'
      }, 0.65);
    }
  }

  // Trigger initial wave on load
  runSerenityEntrance();

  // Re-trigger on language change
  window.addEventListener('vanguard:langchange', () => {
    titleChars = splitTitleIntoLetters();
    descWords = splitDescIntoWords();
    runSerenityEntrance();
  });

  // Re-trigger when returning to home / clicking logo or "#inicio"
  const homeLinks = document.querySelectorAll('a[href="#inicio"], .portal-nav-brand');
  homeLinks.forEach(link => {
    link.addEventListener('click', () => {
      setTimeout(() => {
        runSerenityEntrance();
      }, 150);
    });
  });

  // Also detect scrolling back to top from lower sections
  let wasScrolledDown = false;
  window.addEventListener('scroll', () => {
    const y = window.scrollY;
    if (y > 350) {
      wasScrolledDown = true;
    } else if (y <= 10 && wasScrolledDown) {
      wasScrolledDown = false;
      runSerenityEntrance();
    }
  }, { passive: true });

  // Interactive Mouse Reaction: Subtle float and ambient glow following cursor
  if (heroUniverse && !prefersReducedMotion) {
    heroUniverse.addEventListener('mousemove', (e) => {
      const rect = heroUniverse.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      if (mouseGlow) {
        mouseGlow.style.transform = `translate3d(${x - 180}px, ${y - 180}px, 0)`;
        mouseGlow.style.opacity = '1';
      }

      // Micro reactive lift on title characters when mouse is near
      titleChars.forEach(char => {
        const cRect = char.getBoundingClientRect();
        const charCenterX = cRect.left + cRect.width / 2;
        const charCenterY = cRect.top + cRect.height / 2;
        const dist = Math.hypot(e.clientX - charCenterX, e.clientY - charCenterY);

        if (dist < 65) {
          const intensity = 1 - (dist / 65);
          const lift = intensity * -4;
          char.style.transform = `translate3d(0, ${lift.toFixed(1)}px, 0)`;
          char.style.color = '#ffffff';
          char.style.textShadow = `0 0 ${Math.round(intensity * 14)}px rgba(56, 189, 248, 0.45)`;
        } else {
          char.style.transform = 'translate3d(0, 0, 0)';
          char.style.textShadow = 'none';
        }
      });
    });

    heroUniverse.addEventListener('mouseleave', () => {
      if (mouseGlow) mouseGlow.style.opacity = '0';
      titleChars.forEach(char => {
        char.style.transform = 'translate3d(0, 0, 0)';
        char.style.textShadow = 'none';
      });
    });
  }
}

/* =========================================================
   RUIXEN GRADIENT FOOTER — Aurora Scroll Reactor
   - Scales the gradient floor container on scroll proximity
   - Seamless RAF throttle for 60fps performance
   ========================================================= */
function initRuixenGradientFooter() {
  const footer = document.querySelector('.ruixen-footer');
  if (!footer) return;

  const gradientWrap = footer.querySelector('.ruixen-gradient-container');
  if (!gradientWrap) return;

  const minScale = 0.10;
  const maxScale = 1.0;

  let ticking = false;

  function updateFooterAurora() {
    const docHeight = document.documentElement.scrollHeight;
    const winHeight = window.innerHeight;
    const scrollY = window.scrollY || window.pageYOffset;
    
    // Remaining scroll distance to the absolute bottom of the page
    const remainingScroll = docHeight - winHeight - scrollY;
    
    // Trigger distance: footer height plus half a viewport
    const triggerDistance = Math.max(footer.offsetHeight, winHeight * 0.9);
    
    // Progress is 0 when far above, 1 when at the bottom
    const progress = Math.max(0, Math.min(1, 1 - (remainingScroll / triggerDistance)));
    
    // Smooth power curve for natural organic emergence
    const curvedProgress = Math.pow(progress, 1.25);
    const scale = minScale + ((maxScale - minScale) * curvedProgress);
    
    gradientWrap.style.transform = `scaleY(${scale.toFixed(4)})`;
    ticking = false;
  }

  window.addEventListener('scroll', () => {
    if (!ticking) {
      window.requestAnimationFrame(updateFooterAurora);
      ticking = true;
    }
  }, { passive: true });

  window.addEventListener('resize', () => {
    if (!ticking) {
      window.requestAnimationFrame(updateFooterAurora);
      ticking = true;
    }
  }, { passive: true });

  // Initial call to set correct scale
  updateFooterAurora();
}


